import { useEffect, useRef, useState } from 'react';
import { crm, type ContactRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EntityPicker } from '@/components/EntityPicker';

interface Props {
  /** vorausgewählter Kunde, wenn aus der Kundenakte heraus angelegt */
  contact?: ContactRow | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Einen Termin in Google anlegen.
 *
 * Der Kunde wird als Teilnehmer eingetragen — dadurch bekommt er eine Einladung
 * und der Termin wird beim nächsten Abgleich automatisch seiner Akte
 * zugeordnet, ohne dass jemand etwas verknüpfen muss.
 */
export function EventDialog({ contact = null, onClose, onSaved }: Props) {
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10));
  const [von, setVon] = useState('10:00');
  const [bis, setBis] = useState('11:00');
  const [contactId, setContactId] = useState(contact?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Bitte einen Titel angeben.');
      return;
    }

    const start = new Date(`${datum}T${von}:00`);
    const ende = new Date(`${datum}T${bis}:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(ende.getTime())) {
      setError('Datum oder Uhrzeit ist ungültig.');
      return;
    }
    if (ende <= start) {
      setError('Das Ende liegt vor dem Anfang.');
      return;
    }

    // Der Kunde wird als Teilnehmer eingetragen; seine E-Mail-Adresse kennt
    // dieser Dialog nur, wenn er vorausgewählt wurde — sonst kurz nachfragen.
    const gewaehlt =
      contact && contact.id === contactId
        ? contact
        : contactId
          ? await crm.contacts.get(contactId)
          : null;

    setBusy(true);
    setError(null);
    try {
      const res = await crm.calendar.create({
        title: title.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        startAt: start.toISOString(),
        endAt: ende.toISOString(),
        ...(gewaehlt?.email ? { attendees: [gewaehlt.email] } : {}),
        contactId: contactId || null,
      });
      if (!res.ok) {
        setError(res.error ?? 'Termin liess sich nicht anlegen');
        setBusy(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Termin liess sich nicht anlegen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Neuer Termin"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-md rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">Neuer Termin</h2>

        <div className="mt-4 space-y-3">
          <Field label="Titel">
            <Input
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Vorstellungstermin Musterschule"
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Datum">
              <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
            </Field>
            <Field label="Von">
              <Input type="time" value={von} onChange={(e) => setVon(e.target.value)} />
            </Field>
            <Field label="Bis">
              <Input type="time" value={bis} onChange={(e) => setBis(e.target.value)} />
            </Field>
          </div>

          <Field label="Ort">
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Vor Ort, Videocall …"
            />
          </Field>

          <Field label="Kunde">
            <EntityPicker
              kind="contact"
              value={contactId || null}
              onChange={(id) => setContactId(id ?? '')}
              placeholder="— keiner —"
              disabled={Boolean(contact)}
            />
            <span className="text-xs text-muted-foreground">
              Bekommt eine Einladung und wird dem Termin automatisch zugeordnet.
            </span>
          </Field>

          <Field label="Notiz">
            <textarea
              className="min-h-[4rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Lege an …' : 'Anlegen'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
